import { Maximize } from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import { RemoteFeedObject } from 'vcs-common';
import classNames from 'classnames';

import { getSharingPersonName } from '../../../features/meet/meet.adapters';
import { Spinner, IconButton } from '../../../shared/ui';

interface ScreenShareViewerProps {
  screenShareFeed: RemoteFeedObject;
  psychologistName?: string;
  clientName?: string;
  screenShareOwnerName?: string;
  isPsychologist?: boolean;
}

export const ScreenShareViewer: FC<ScreenShareViewerProps> = ({
  screenShareFeed,
  psychologistName,
  clientName,
  screenShareOwnerName,
  isPsychologist,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const sharingPersonName = getSharingPersonName(
    Boolean(isPsychologist),
    psychologistName,
    clientName,
    screenShareOwnerName,
  );

  useEffect(() => {
    if (videoRef.current) {
      screenShareFeed.attachVideo(videoRef.current);
      setIsLoading(true);
      setIsVideoReady(false);
    }
    return () => {
      screenShareFeed.remove?.();
    };
  }, [screenShareFeed]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      setIsVideoReady(true);
      setIsLoading(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
      setIsVideoReady(false);
    };

    const handleWaiting = () => {
      setIsLoading(true);
    };

    const handlePlaying = () => {
      setIsLoading(false);
      setIsVideoReady(true);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);

    // Проверяем, если видео уже загружено
    if (video.readyState >= 2) {
      setIsVideoReady(true);
      setIsLoading(false);
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
    };
  }, [screenShareFeed]);

  const handleFullscreenClick = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen().catch((err) => {
          console.error('Ошибка при переходе в полноэкранный режим:', err);
        });
      } else if ((videoRef.current as any).webkitRequestFullscreen) {
        (videoRef.current as any).webkitRequestFullscreen();
      } else if ((videoRef.current as any).mozRequestFullScreen) {
        (videoRef.current as any).mozRequestFullScreen();
      } else if ((videoRef.current as any).msRequestFullscreen) {
        (videoRef.current as any).msRequestFullscreen();
      }
    }
  };

  return (
    <div className="relative w-full h-full bg-ink-muted rounded-card overflow-hidden pl-6 pr-6 flex flex-col">
      {/* Заголовок - показываем сразу */}
      <div className="flex-shrink-0 pt-[10px] pb-[10px]">
        <div className="items-center rounded-control flex px-[10px] h-[56px] bg-ink-muted z-10">
          <svg
            width="24"
            height="16"
            viewBox="0 0 24 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-2 flex-shrink-0"
          >
            <path
              d="M3 0C2.20435 0 1.44129 0.294999 0.87868 0.820101C0.31607 1.3452 0 2.05739 0 2.8V13.2C0 14.7456 1.344 16 3 16H21C21.7957 16 22.5587 15.705 23.1213 15.1799C23.6839 14.6548 24 13.9426 24 13.2V2.8C24 2.05739 23.6839 1.3452 23.1213 0.820101C22.5587 0.294999 21.7957 0 21 0H3ZM12.6069 3.4336L16.0354 6.6336C16.1151 6.70798 16.1783 6.79628 16.2215 6.89347C16.2646 6.99065 16.2868 7.09481 16.2868 7.2C16.2868 7.30519 16.2646 7.40935 16.2215 7.50653C16.1783 7.60372 16.1151 7.69202 16.0354 7.7664C15.9557 7.84078 15.8611 7.89978 15.757 7.94004C15.6529 7.98029 15.5413 8.00101 15.4286 8.00101C15.3159 8.00101 15.2043 7.98029 15.1001 7.94004C14.996 7.89978 14.9014 7.84078 14.8217 7.7664L12.8571 5.9312V12C12.8571 12.2122 12.7668 12.4157 12.6061 12.5657C12.4453 12.7157 12.2273 12.8 12 12.8C11.7727 12.8 11.5547 12.7157 11.3939 12.5657C11.2332 12.4157 11.1429 12.2122 11.1429 12V5.9312L9.17829 7.7664C9.01734 7.91662 8.79904 8.00101 8.57143 8.00101C8.34381 8.00101 8.12552 7.91662 7.96457 7.7664C7.80362 7.61618 7.7132 7.41244 7.7132 7.2C7.7132 6.98756 7.80362 6.78382 7.96457 6.6336L11.3931 3.4336C11.4728 3.3591 11.5674 3.29999 11.6715 3.25966C11.7756 3.21933 11.8873 3.19857 12 3.19857C12.1127 3.19857 12.2244 3.21933 12.3285 3.25966C12.4326 3.29999 12.5272 3.3591 12.6069 3.4336Z"
              fill="white"
            />
          </svg>
          <div className="text-white">{`${sharingPersonName} показывает экран`}</div>
        </div>
      </div>

      {/* Видео контейнер — на всю оставшуюся высоту */}
      <div className="relative flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden">
        <div className="relative min-h-0 min-w-0 h-full max-w-full shrink-0 flex items-center justify-center">
          {/* Видео элемент - всегда в DOM, но может быть скрыто */}
          <video
            ref={videoRef}
            className={classNames('hide-video-controls block object-contain h-full w-auto max-w-full', {
              'opacity-0': isLoading,
              'opacity-100': !isLoading && isVideoReady,
            })}
            autoPlay
            playsInline
          />

          {/* Loader - показываем пока загружается */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="flex flex-col items-center justify-center">
                <Spinner size="xl" className="text-primary" />
                <p className="mt-4 text-white text-ui">Загрузка экрана...</p>
              </div>
            </div>
          )}

          {/* Кнопка fullscreen - показываем только когда видео готово */}
          {isVideoReady && (
            <IconButton
              label="Развернуть на весь экран"
              size="md"
              variant="overlay"
              className="absolute top-4 right-4 z-20"
              onClick={handleFullscreenClick}
            >
              <Maximize size={18} aria-hidden />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};
